import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { filter, map, take } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ResponseApi } from '../../../dtos/ResponseApi';
import { LoginDto } from '../../../dtos/core/account/LoginDto';
import { AccountResponse } from '../../../dtos/core/account/AccountResponse';
import { CreateAccountDto } from '../../../dtos/core/account/CreateAccountDto';
import { TokenResponse } from '../../../dtos/core/account/TokenResponse';
import { UserToken } from '../../../dtos/core/auth/UserToken';
import { ChangeClienteDto } from '../../../dtos/core/account/ChangeClienteDto';
import { ForgotPasswordDto } from '../../../dtos/core/account/ForgotPasswordDto';
import { PasswordResponse } from '../../../dtos/core/account/PasswordResponse';
import { ChangePasswordDto } from '../../../dtos/core/account/ChangePasswordDto';
import { ResetPasswordDto } from '../../../dtos/core/account/ResetPasswordDto';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUserSubject: BehaviorSubject<UserToken | null>;
  public currentUser: Observable<UserToken | null>;
  private baseUrl = `${environment.apiUrl}/core/Account`;


  constructor(private http: HttpClient, private jwtHelper: JwtHelperService) {
    this.currentUserSubject = new BehaviorSubject<UserToken | null>(
      this.buildUserFromToken(this.getToken())
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): UserToken | null {
    return this.currentUserSubject.value;
  }

  loginJwt(login: LoginDto): Observable<ResponseApi<TokenResponse>> {
    return this.http.post<ResponseApi<TokenResponse>>(`${this.baseUrl}/login`, login);
  }

  login(login: LoginDto): Observable<UserToken> {
    return this.loginJwt(login).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error('Erro durante o login: ' + response.message);
        }

        const token = response.data.accessToken;
        const user = this.buildUserFromDecodedToken(token, response.data.tokenType);

        localStorage.setItem('accessToken', token);
        this.currentUserSubject.next(user);

        return user;
      })
    );
  }


  logout() {
    localStorage.removeItem('accessToken');
    this.currentUserSubject.next(null!);
    return of({ success: false });
  }


  register(registro: CreateAccountDto): Observable<ResponseApi<AccountResponse>> {
    return this.http.post<ResponseApi<AccountResponse>>(`${this.baseUrl}/register`, registro);
  }

  getToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getTokenDecoded(): any {
    return this.jwtHelper.decodeToken(this.getToken()!);
  }

  isTokenExpired(): boolean {
    const token = this.getToken();

    if (!token) return true;

    return this.jwtHelper.isTokenExpired(token);
  }


  private buildUserFromToken(token: string | null): UserToken | null {
    if (!token) return null;

    if (this.jwtHelper.isTokenExpired(token)) {
      localStorage.removeItem('accessToken');
      return null;
    }

    return this.buildUserFromDecodedToken(token, 'Bearer');
  }

  private buildUserFromDecodedToken(
    token: string,
    tokenType: string
  ): UserToken {

    const decoded = this.jwtHelper.decodeToken(token);

    const rolesArray = decoded['role'] || [];
    const roles = Array.isArray(rolesArray) ? rolesArray : [rolesArray];

    return {
      token,
      tokenType,
      expiration: decoded['exp'],
      usuarioId: decoded['UsuarioId'],
      nome: decoded['Nome'],
      nomeSocial: decoded['NomeSocial'],
      email: decoded['Email'],
      nomeFantasia: decoded['NomeFantasia'],
      clienteId: decoded['ClienteId'],
      clienteSlug: decoded['ClienteSlug'],
      clienteUrl: decoded['ClienteUrl'],
      perfilId: decoded['PerfilId'],
      perfilNome: decoded['PerfilNome'],
      multiplosClientes: decoded['MultiplosClientes'] === '1',
      codigoSigem: decoded['CodigoSigem'],
      codigoSiga: decoded['CodigoSiga'],
      temaConfiguracao: decoded['TemaConfiguracao'],
      temaNome: decoded['TemaNome'],
      roles
    };
  }


  trocarClienteJwt(login: ChangeClienteDto): Observable<ResponseApi<TokenResponse>> {
    return this.http.post<ResponseApi<TokenResponse>>(`${this.baseUrl}/ChangeCliente`, login);
  }

  trocarCliente(login: ChangeClienteDto): Observable<UserToken> {
    return this.trocarClienteJwt(login).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error('Erro durante o ChangeCliente: ' + response.message);
        }

        const token = response.data.accessToken;
        const user = this.buildUserFromDecodedToken(token, response.data.tokenType);

        localStorage.removeItem('accessToken');
        localStorage.setItem('accessToken', token);
        this.currentUserSubject.next(user);

        return user;
      })
    );
  }

  waitForUser(): Observable<UserToken> {
    return this.currentUser.pipe(
      filter((user): user is UserToken => !!user),
      take(1)
    );
  }

  changePassword(request: ChangePasswordDto): Observable<ResponseApi<PasswordResponse[]>> {
    return this.http.post<ResponseApi<PasswordResponse[]>>(`${this.baseUrl}/ChangePassword`, request);
  }

  forgotPassword(request: ForgotPasswordDto): Observable<ResponseApi<PasswordResponse[]>> {
    return this.http.post<ResponseApi<PasswordResponse[]>>(`${this.baseUrl}/ForgotPassword`, request);
  }

  resetPassword(request: ResetPasswordDto): Observable<ResponseApi<PasswordResponse[]>> {
    return this.http.post<ResponseApi<PasswordResponse[]>>(`${this.baseUrl}/ResetPassword`, request);
  }
}
